import classNames from "classnames";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import {
  useGetAutoEmailsQuery,
  useGetSavedRepliesQuery,
} from "../../slices/SupportSlice";
import { ReplyCard, ReplyForm } from "./SavedRepliesPage";
import { PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

const CARD = "mb-5 rounded-[14px] border border-[#EDEFF3] bg-white";

/**
 * Help desk settings › "Email templates" (owner 2026-10-30, Loom 3007fc28: "add it in the settings here where we can
 * add and modify email templates and see the list of all the current templates"). One place for both kinds:
 * reply templates (the saved replies you insert in a case, added / edited / deleted right here) and the
 * automatic emails (each opens its editor).
 */
export const EmailTemplatesCard = () => {
  const { data: replies } = useGetSavedRepliesQuery();
  const { data: autoEmails } = useGetAutoEmailsQuery();
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const list = replies || [];

  return (
    <div className={CARD} data-testid={"email-templates"}>
      <div className={"border-b border-[#EDEFF3] px-[18px] py-4"}>
        <h2 className={"text-[17px] font-extrabold text-[#1F2433]"}>
          Email templates
        </h2>
        <p className={"mt-1 text-[13.5px] leading-relaxed text-[#5D6475]"}>
          All your templates in one place. Reply templates are inserted in a
          case with &ldquo;Insert saved reply&rdquo; (the AI also uses them);
          automatic emails go out by themselves as drafts for you to review.
        </p>
      </div>

      <div className={"px-[18px] py-4"}>
        <div className={"mb-2.5 flex flex-wrap items-center justify-between gap-2"}>
          <div className={"text-[14.5px] font-extrabold text-[#1F2433]"}>
            Reply templates{" "}
            <span className={"font-semibold text-[#7A7F8C]"}>
              ({list.length})
            </span>
          </div>
          {editing !== "new" && (
            <button
              type={"button"}
              className={PRIMARY_BUTTON}
              onClick={() => setEditing("new")}>
              + New template
            </button>
          )}
        </div>
        {editing === "new" && <ReplyForm onClose={() => setEditing(null)} />}
        {list.length === 0 && editing !== "new" ? (
          <div
            className={
              "rounded-[12px] border border-dashed border-[#DCE2EA] px-4 py-6 text-center text-[13.5px] text-[#5D6475]"
            }>
            No reply templates yet. Good first ones: how to reset a password,
            how to import clients, how to add a team member.
          </div>
        ) : (
          <div className={"flex flex-col gap-2.5"}>
            {list.map((reply) =>
              editing === reply.id ? (
                <ReplyForm
                  key={reply.id}
                  reply={reply}
                  onClose={() => setEditing(null)}
                />
              ) : (
                <ReplyCard
                  key={reply.id}
                  reply={reply}
                  onEdit={() => setEditing(reply.id)}
                />
              ),
            )}
          </div>
        )}
      </div>

      <div className={"border-t border-[#EDEFF3] px-[18px] py-4"}>
        <div className={"mb-2.5 text-[14.5px] font-extrabold text-[#1F2433]"}>
          Automatic emails{" "}
          <span className={"font-semibold text-[#7A7F8C]"}>
            ({autoEmails?.length ?? 0})
          </span>
        </div>
        <div className={"flex flex-col divide-y divide-[#EDEFF3]"}>
          {(autoEmails || []).map((t) => (
            <div
              key={t.key}
              className={"flex items-center justify-between gap-3 py-2.5"}>
              <div className={"min-w-0"}>
                <div
                  className={
                    "flex items-center gap-2 text-[14px] font-bold text-[#1F2433]"
                  }>
                  <span className={"truncate"}>{t.name}</span>
                  <span
                    className={classNames(
                      "shrink-0 rounded-full px-2 py-px text-[11px] font-bold",
                      t.enabled
                        ? "bg-[#E9F9F1] text-[#147A4B]"
                        : "bg-[#EDEFF3] text-[#5D6475]",
                    )}>
                    {t.enabled ? "On" : "Off"}
                  </span>
                </div>
                <div className={"truncate text-[12.5px] text-[#5D6475]"}>
                  {t.subject || t.when}
                </div>
              </div>
              <Link
                to={`/support/automatic-emails/${t.key}`}
                className={classNames(SMALL_BUTTON, "shrink-0")}>
                Edit
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
